import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import DatePicker from "@/components/base/DatePicker";
import { submitWeddingInquiry } from "@/lib/villaApi";
import { img } from "@/lib/images";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };

const WEDDING_TYPE_OPTIONS = [
  { value: "general", key: "general" },
  { value: "standesamt", key: "standesamt" },
  { value: "ohne", key: "ohne" },
  { value: "eheversprechen", key: "eheversprechen" },
  { value: "jubilaeum", key: "jubilaeum" },
];

export default function WeddingInquiryPage() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [date, setDate] = useState("");

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 3);
  const maxDateStr = maxDate.toISOString().split("T")[0];

  const inputClass =
    "w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:border-accent-600 transition-colors";

  const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem("company_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: t("wedding_form_success") });
      form.reset();
      setDate("");
      return;
    }

    const fd = new FormData(form);
    fd.delete("company_alt");

    const body: Record<string, unknown> = {};
    fd.forEach((value, key) => {
      if (typeof value === "string") body[key] = value;
    });

    const required: Array<[string, string]> = [
      ["wedding_type", isDe ? "Art der Hochzeit" : "Type of wedding"],
      ["wedding_date", isDe ? "Wunschdatum" : "Desired date"],
      ["guests", isDe ? "Anzahl Gäste" : "Number of guests"],
      ["firstname", isDe ? "Vorname(n)" : "First name(s)"],
      ["lastname", isDe ? "Familienname(n)" : "Last name(s)"],
      ["email", isDe ? "E-Mail-Adresse" : "Email address"],
      ["phone", isDe ? "Telefon / WhatsApp" : "Phone / WhatsApp"],
    ];
    for (const [key, label] of required) {
      if (!String(body[key] ?? "").trim()) {
        setStatus({
          type: "error",
          msg: isDe
            ? `Bitte füllen Sie das Pflichtfeld „${label}" aus.`
            : `Please fill in the required field "${label}".`,
        });
        return;
      }
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { error } = await submitWeddingInquiry({
        wedding_type: String(body.wedding_type ?? ""),
        wedding_date: String(body.wedding_date ?? ""),
        guests: String(body.guests ?? ""),
        firstname: String(body.firstname ?? ""),
        lastname: String(body.lastname ?? "").toUpperCase(),
        email: String(body.email ?? ""),
        phone: (body.phone as string) || null,
        message: (body.message as string) || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        setStatus({ type: "error", msg: error.message || t("form_error") });
        return;
      }

      setStatus({ type: "success", msg: t("wedding_form_success") });
      form.reset();
      setDate("");
    } catch {
      setStatus({ type: "error", msg: t("form_error") });
    }
  };

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("wedding_inquiry_eyebrow")}
          title={t("wedding_inquiry_title")}
          subtitle={t("wedding_inquiry_sub")}
          image={img("weddings/wedding-inquiry/header-wedding-inquiry-01.jpg")}
          imageAlt={t("wedding_inquiry_title")}
        />

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-5">
                  {/* Art der Hochzeit */}
                  <div>
                    <label htmlFor="wedding_type" className={labelClass}>
                      {t("wedding_form_type_label")}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <select
                      id="wedding_type"
                      name="wedding_type"
                      required
                      className={`${inputClass} cursor-pointer`}
                    >
                      <option value="">{t("wedding_form_type_ph")}</option>
                      {WEDDING_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(`wedding_form_type_${o.key}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Wunschdatum */}
                  <div>
                    <label htmlFor="wedding_date" className={labelClass}>
                      {t("wedding_form_date_label")}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <DatePicker
                      name="wedding_date"
                      value={date}
                      min={today}
                      max={maxDateStr}
                      isDe={isDe}
                      placeholder={isDe ? "Datum wählen" : "Select date"}
                      onChange={setDate}
                    />
                  </div>

                  {/* Anzahl Gäste */}
                  <div>
                    <label htmlFor="wedding_guests" className={labelClass}>
                      {t("wedding_form_guests_label")}
                      <span className="text-accent-600 ml-1">*</span>
                    </label>
                    <input
                      id="wedding_guests"
                      name="guests"
                      type="number"
                      min={2}
                      max={200}
                      required
                      placeholder="2"
                      className={inputClass}
                    />
                  </div>

                  {/* Hinweis zu Namen */}
                  <p className="text-sm text-foreground-600">
                    {isDe
                      ? <>Bitte bei zwei verschiedenen Namen immer beide angeben.<br />Max / Erika&nbsp;&nbsp;&nbsp;&nbsp;MUSTERMANN / MÜLLER</>
                      : <>If there are two different names, please always provide both.<br />John / Jane&nbsp;&nbsp;&nbsp;&nbsp;DOE / SMITH</>}
                  </p>

                  {/* Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="wedding_firstname" className={labelClass}>
                        {t("wedding_form_firstname_label")}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <input
                        id="wedding_firstname"
                        name="firstname"
                        type="text"
                        required
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="wedding_lastname" className={labelClass}>
                        {t("wedding_form_lastname_label")}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <input
                        id="wedding_lastname"
                        name="lastname"
                        type="text"
                        required
                        className={`${inputClass} uppercase`}
                      />
                    </div>
                  </div>

                  {/* Kontakt */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="wedding_email" className={labelClass}>
                        {t("form_email")}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <input
                        id="wedding_email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder={t("form_email_ph")}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="wedding_phone" className={labelClass}>
                        {t("form_phone")}
                        <span className="text-accent-600 ml-1">*</span>
                      </label>
                      <input
                        id="wedding_phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder={t("form_phone_ph")}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Nachricht */}
                  <div>
                    <label htmlFor="wedding_message" className={labelClass}>
                      {t("wedding_form_message_label")}
                    </label>
                    <textarea
                      id="wedding_message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder={t("wedding_form_message_ph")}
                      className={`${inputClass} resize-none`}
                    />
                    <p className="text-xs text-foreground-500 mt-1">
                      {isDe ? "Maximal 500 Zeichen" : "Maximum 500 characters"}
                    </p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="company_alt"
                    className="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status.type === "sending"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium px-10 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {status.type === "sending" && (
                      <i className="ri-loader-4-line animate-spin"></i>
                    )}
                    {status.type === "sending"
                      ? t("wedding_form_sending")
                      : t("wedding_form_submit")}
                  </button>
                  <BookingCompanyNotice isDe={isDe} />

                  {/* Status messages */}
                  {status.type === "success" && (
                    <div className="text-sm font-medium text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-4 py-3">
                      {status.msg}
                    </div>
                  )}
                  {status.type === "error" && (
                    <div className="text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
                      {status.msg}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}